// Morning / Afternoon / Evening — how families actually say when they can help.
//
// "Tuesday afternoon" is the unit of thought for a non-technical member
// (AVL-002), and the plan's usability promise is that they can mark it in under
// 30 seconds without instruction. Everything downstream still needs concrete
// instants (plan §28.12: "Support windows must remain concrete instants even
// when displayed as Morning/Afternoon/Evening"), so this module is the exact,
// per-date translation between the two.
//
// The kernel owns the DEFINITIONS. `packages/design-system` consumes the
// identifiers below and renders labels; it never decides what a block means.

import type { Instant, TimeZoneId, WallClock } from "./types.js";
import { resolveWallClock } from "./zone.js";
import type { LocalDate } from "./local-date.js";
import type { TimeWindow } from "./window.js";

/**
 * The three blocks (AVL-002).
 *
 * Stable identifiers, not labels: `@herefor/product-language` owns the words a
 * person reads, and the design system keys off these.
 */
export type TimeBlockId = "morning" | "afternoon" | "evening";

/** All three, in the order they occur in a day. Useful for rendering a grid. */
export const TIME_BLOCK_IDS: readonly TimeBlockId[] = ["morning", "afternoon", "evening"];

/** A local wall-clock time of day, `HH:MM` in 24-hour form. */
export type LocalTimeOfDay = string & { readonly __brand: unique symbol };

const LOCAL_TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Narrow an `HH:MM` string to a {@link LocalTimeOfDay}. */
export function localTimeOfDay(value: string): LocalTimeOfDay {
  if (!LOCAL_TIME_PATTERN.test(value)) {
    throw new RangeError(`LocalTimeOfDay must be HH:MM in 24-hour form, got ${value}`);
  }
  return value as LocalTimeOfDay;
}

/** One block's wall-clock bounds. Half-open, matching {@link TimeWindow}. */
export interface BlockBounds {
  readonly startLocal: LocalTimeOfDay;
  readonly endLocal: LocalTimeOfDay;
}

/**
 * A circle's block boundaries (plan §28.4 `circles.blockDefinitions`).
 *
 * Circle-configurable, because a family that starts its day at 06:00 should not
 * be told 08:00 is "morning". The interface does not show these boundaries
 * unless somebody asks "What do these mean?" (plan §11.4).
 */
export interface BlockDefinitions {
  readonly morning: BlockBounds;
  readonly afternoon: BlockBounds;
  readonly evening: BlockBounds;
}

/**
 * The plan's initial defaults (plan §11.4): Morning 08:00–12:00, Afternoon
 * 12:00–17:00, Evening 17:00–21:00.
 *
 * A starting point for circle creation, not a constant to reach for elsewhere —
 * always read the circle's own definitions, or a circle that customised them
 * gets somebody else's idea of the afternoon.
 */
export const DEFAULT_BLOCK_DEFINITIONS: BlockDefinitions = {
  morning: { startLocal: "08:00" as LocalTimeOfDay, endLocal: "12:00" as LocalTimeOfDay },
  afternoon: { startLocal: "12:00" as LocalTimeOfDay, endLocal: "17:00" as LocalTimeOfDay },
  evening: { startLocal: "17:00" as LocalTimeOfDay, endLocal: "21:00" as LocalTimeOfDay },
};

/**
 * What the clocks did to a block on a DST transition day.
 *
 * `null` on all but two days a year. Carried rather than hidden so a surface
 * that wants to explain a short or long block can, while the ordinary path stays
 * a plain window.
 */
export type BlockDstAdjustment =
  /** The block's nominal start does not exist; it begins at the first time that does. */
  | "start_did_not_exist"
  /** The block's nominal end does not exist; it ends at the first time that does. */
  | "end_did_not_exist"
  /** The nominal start happens twice; the block takes the earlier. */
  | "start_ambiguous"
  /** The nominal end happens twice; the block takes the later. */
  | "end_ambiguous";

export interface ResolvedBlock {
  readonly blockId: TimeBlockId;
  readonly date: LocalDate;
  readonly window: TimeWindow;
  /** Non-null only on a DST transition day. See {@link BlockDstAdjustment}. */
  readonly dstAdjustment: BlockDstAdjustment | null;
}

/**
 * Resolve a block to concrete instants on one specific date.
 *
 * ## Why this is per-date and not a constant offset
 *
 * On an ordinary Europe/London day, Morning is four hours. On 29 March 2026 the
 * clocks jump 01:00 → 02:00, and on 25 October 2026 01:00–02:00 happens twice.
 * A block defined across those hours is genuinely shorter or longer that day,
 * and the only way to know is to ask the zone about that exact date.
 *
 * The default blocks (08:00 / 12:00 / 17:00 / 21:00) never touch a Europe/London
 * transition, so `dstAdjustment` is `null` in practice today. It is implemented
 * anyway because block boundaries are circle-configurable, and a circle that
 * sets an early-morning block would otherwise hit a silently wrong window twice
 * a year.
 *
 * ## The disambiguation convention
 *
 * When a nominal boundary is ambiguous (the fall-back hour), the block takes the
 * **earlier** instant for its start and the **later** for its end — the maximal
 * span. A person marking that local hour available did not distinguish between
 * two occurrences of "01:30" they were never shown; covering both is the reading
 * that matches what they meant.
 *
 * When a nominal boundary does not exist (the spring-forward gap), the block
 * moves to the first instant that does, so the block is simply shorter that day.
 */
export function resolveBlock(
  blockId: TimeBlockId,
  date: LocalDate,
  zone: TimeZoneId,
  definitions: BlockDefinitions,
): ResolvedBlock {
  const bounds = definitions[blockId];

  const start = resolveBoundary(date, bounds.startLocal, zone, "start");
  const end = resolveBoundary(date, bounds.endLocal, zone, "end");

  return {
    blockId,
    date,
    window: { startsAt: start.instant, endsAt: end.instant },
    dstAdjustment: start.adjustment ?? end.adjustment,
  };
}

/** Resolve all three blocks for a date, in day order. */
export function resolveAllBlocks(
  date: LocalDate,
  zone: TimeZoneId,
  definitions: BlockDefinitions,
): readonly ResolvedBlock[] {
  return TIME_BLOCK_IDS.map((blockId) => resolveBlock(blockId, date, zone, definitions));
}

interface ResolvedBoundary {
  readonly instant: Instant;
  readonly adjustment: BlockDstAdjustment | null;
}

function resolveBoundary(
  date: LocalDate,
  timeOfDay: LocalTimeOfDay,
  zone: TimeZoneId,
  end: "start" | "end",
): ResolvedBoundary {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const [hour, minute] = timeOfDay.split(":").map(Number) as [number, number];

  const wall: WallClock = { year, month, day, hour, minute };
  const resolved = resolveWallClock(wall, zone);

  if (resolved.kind === "ok") {
    return { instant: resolved.instant, adjustment: null };
  }

  if (resolved.kind === "ambiguous") {
    return {
      instant: end === "start" ? resolved.earlier : resolved.later,
      adjustment: end === "start" ? "start_ambiguous" : "end_ambiguous",
    };
  }

  // The nominal time was skipped by a forward transition. Walk to the first
  // wall time that does exist, so the block is shorter rather than undefined.
  return {
    instant: firstExistingAfter(wall, zone),
    adjustment: end === "start" ? "start_did_not_exist" : "end_did_not_exist",
  };
}

function firstExistingAfter(wall: WallClock, zone: TimeZoneId): Instant {
  const minuteOfDay = wall.hour * 60 + wall.minute;

  // A DST gap is at most a couple of hours anywhere in the IANA database; a full
  // day of probing is generous and still bounded.
  for (let offset = 1; offset <= 24 * 60; offset += 1) {
    const candidateMinute = minuteOfDay + offset;
    if (candidateMinute >= 24 * 60) break;

    const candidate: WallClock = {
      year: wall.year,
      month: wall.month,
      day: wall.day,
      hour: Math.floor(candidateMinute / 60),
      minute: candidateMinute % 60,
    };
    const resolved = resolveWallClock(candidate, zone);
    if (resolved.kind === "ok") return resolved.instant;
    if (resolved.kind === "ambiguous") return resolved.earlier;
  }

  throw new RangeError(
    `No wall-clock time exists at or after ${String(wall.hour)}:${String(wall.minute)} on ` +
      `${String(wall.year)}-${String(wall.month)}-${String(wall.day)} in ${zone}`,
  );
}
